import axios from 'axios';
import { Globals } from '@/modules/Globals';
import type { IBranch } from '@/interfaces/IBranch';
import type {
  IGeocodeQuery,
  IGeoResult,
  IPlaceDetails,
  IPlaceDetailsQuery,
  IPlacesAutocompleteQuery,
  IPlaceSuggestion,
  IReverseGeocodeQuery,
  ISetupOrdersDto,
  ISetupPatchDto,
  ISlugQuery,
  ISlugStatus,
  IStoreSetupResume,
  IStoreSetupStatus,
} from '@/interfaces/IStoreSetup';

const apiUrl = Globals.apiPath + '/store-setup';

/** Query string of the defined, non-null params, in the given order. */
function queryString(
  params: Record<string, string | number | null | undefined>
): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) query.set(key, String(value));
  }
  return query.toString();
}

/** `/store-setup/*`: store creation flow, setup state and «Pedidos» (plan §6.3–§6.7). */
export class StoreSetupService {
  static async slug(q: ISlugQuery): Promise<ISlugStatus> {
    const res = await axios.get<ISlugStatus>(
      `${apiUrl}/slug?${queryString({
        name: q.name,
        city: q.city,
        slug: q.slug,
      })}`
    );
    return res.data;
  }

  static async placesAutocomplete(
    q: IPlacesAutocompleteQuery
  ): Promise<IPlaceSuggestion[]> {
    const res = await axios.get<{ suggestions: IPlaceSuggestion[] }>(
      `${apiUrl}/places/autocomplete?${queryString({
        input: q.input,
        country: q.country,
        sessionToken: q.sessionToken,
        language: q.language,
      })}`
    );
    return res.data.suggestions;
  }

  static async placeDetails(
    placeId: string,
    q: IPlaceDetailsQuery
  ): Promise<IPlaceDetails> {
    const res = await axios.get<IPlaceDetails>(
      `${apiUrl}/places/${encodeURIComponent(placeId)}?${queryString({
        sessionToken: q.sessionToken,
        language: q.language,
      })}`
    );
    return res.data;
  }

  static async geocode(q: IGeocodeQuery): Promise<IGeoResult | null> {
    const res = await axios.get<{ result: IGeoResult | null }>(
      `${apiUrl}/geocode?${queryString({
        address: q.address,
        country: q.country,
        language: q.language,
      })}`
    );
    return res.data.result ?? null;
  }

  static async reverseGeocode(
    q: IReverseGeocodeQuery
  ): Promise<IGeoResult | null> {
    const res = await axios.get<{ result: IGeoResult | null }>(
      `${apiUrl}/reverse-geocode?${queryString({
        lat: q.lat,
        lng: q.lng,
        language: q.language,
      })}`
    );
    return res.data.result ?? null;
  }

  /** The latest unfinished flow store of the user; null when there is none (204). */
  static async resume(): Promise<IStoreSetupResume | null> {
    const res = await axios.get<IStoreSetupResume | ''>(`${apiUrl}/resume`);
    if (res.status === 204 || !res.data) return null;
    return res.data;
  }

  static async status(restaurantId: number): Promise<IStoreSetupStatus> {
    const res = await axios.get<IStoreSetupStatus>(`${apiUrl}/${restaurantId}`);
    return res.data;
  }

  /** 204. */
  static async patch(
    restaurantId: number,
    body: ISetupPatchDto
  ): Promise<void> {
    await axios.patch(`${apiUrl}/${restaurantId}`, body);
  }

  static async saveOrders(
    restaurantId: number,
    dto: ISetupOrdersDto
  ): Promise<{ branch: IBranch }> {
    const res = await axios.put<{ branch: IBranch }>(
      `${apiUrl}/${restaurantId}/orders`,
      dto
    );
    return res.data;
  }

  /**
   * Marks the dashboard's QR step done after a QR download. Fire and forget:
   * every error is swallowed (the API answers 204 for stores created outside
   * the flow, and a failed mark must never disturb the download).
   */
  static async markQrDone(restaurantId: number): Promise<void> {
    try {
      await StoreSetupService.patch(restaurantId, { qrDone: true });
    } catch {
      // Intentionally ignored (see above).
    }
  }
}
